// Shared fill-color control for the symbol exporters (single + bulk). The color
// is baked into the downloaded SVG/PNG — plain copied Unicode can't carry color,
// so this only affects image exports.

/** Default export ink — near-black, matches the historical hardcoded fill. */
export const DEFAULT_FILL = "#111111";

// The `swatch` class must be a literal `bg-[#…]` string so Tailwind's JIT
// scanner emits it (a templated `bg-[${hex}]` would never build).
const PRESET_COLORS: ReadonlyArray<{ hex: string; label: string; swatch: string }> = [
  { hex: "#111111", label: "Black", swatch: "bg-[#111111]" },
  { hex: "#ffffff", label: "White", swatch: "bg-[#ffffff]" },
  { hex: "#7c3aed", label: "Violet", swatch: "bg-[#7c3aed]" },
  { hex: "#ec4899", label: "Pink", swatch: "bg-[#ec4899]" },
  { hex: "#ef4444", label: "Red", swatch: "bg-[#ef4444]" },
  { hex: "#f59e0b", label: "Amber", swatch: "bg-[#f59e0b]" },
  { hex: "#10b981", label: "Green", swatch: "bg-[#10b981]" },
  { hex: "#3b82f6", label: "Blue", swatch: "bg-[#3b82f6]" },
];

interface Props {
  color: string;
  onChange: (color: string) => void;
  label?: string;
}

export function FillColorPicker({ color, onChange, label = "Fill color" }: Props) {
  return (
    <fieldset>
      <legend className="text-xs font-medium text-muted-foreground">{label}</legend>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {PRESET_COLORS.map((c) => {
          const active = color.toLowerCase() === c.hex;
          return (
            <button
              key={c.hex}
              type="button"
              onClick={() => onChange(c.hex)}
              aria-label={`Fill color ${c.label}`}
              aria-pressed={active}
              className={`h-7 w-7 rounded-full border border-border transition ${c.swatch} ${
                active ? "ring-2 ring-ring ring-offset-2 ring-offset-background" : "hover:scale-110"
              }`}
            />
          );
        })}
        <label className="ml-1 inline-flex items-center gap-2 rounded-md border border-border bg-surface/60 px-2 py-1 text-xs text-muted-foreground">
          Custom
          <input
            type="color"
            value={color}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Custom fill color"
            className="h-6 w-8 cursor-pointer rounded bg-transparent [color-scheme:dark]"
          />
        </label>
      </div>
    </fieldset>
  );
}
